import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import api from "../api/axios";
import "../assets/styles/page.css";
import "../assets/styles/directory-tables.css";

import { GrPrevious, GrNext } from "react-icons/gr";
import { TbEyeSearch, TbPackage } from "react-icons/tb";

const PAGE_SIZE = 10;
const PAGE_NAME = "Customers";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [...new Set([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ])]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);

  return pages.flatMap((page, index) =>
    index > 0 && page - pages[index - 1] > 1
      ? [`ellipsis-${page}`, page]
      : [page]
  );
};

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const Customers = () => {
  const [searchParams] = useSearchParams();
  const linkedCustomerId = searchParams.get("user_id");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [viewSidebarOpen, setViewSidebarOpen] = useState(false);
  const [ordersSidebarOpen, setOrdersSidebarOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });

  const orderSummary = useMemo(
    () => ({
      total: customerOrders.reduce(
        (sum, order) => sum + Number(order.total_amount || 0),
        0
      ),
      items: customerOrders.reduce(
        (sum, order) =>
          sum +
          (order.items || []).reduce(
            (itemTotal, item) => itemTotal + Number(item.quantity || 0),
            0
          ),
        0
      ),
    }),
    [customerOrders]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCustomers = async (page = 1) => {
    try {
      setLoading(true);

      const res = await api.get("/admin/users/customers", {
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          status: status || undefined,
        },
        headers: getPermissionHeaders("view"),
      });

      setCustomers(res.data?.data || []);
      setPagination(
        res.data?.pagination || {
          page,
          total: 0,
          totalPages: 1,
        }
      );
    } catch (error) {
      showRequestError(error, "Failed to fetch customers.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerOrders = async (userId) => {
    try {
      setOrdersLoading(true);

      const res = await api.get(`/admin/orders/user/${userId}`, {
        params: {
          limit: 50,
        },
        headers: getPermissionHeaders("view"),
      });

      setCustomerOrders(res.data?.data || []);
    } catch (error) {
      setCustomerOrders([]);
      showRequestError(error, "Failed to fetch customer orders.");
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers(1);
  }, [debouncedSearch, status]);

  const openViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setCustomerOrders([]);
    setOrdersSidebarOpen(false);
    setViewSidebarOpen(true);
    await fetchCustomerOrders(customer.user_id);
  };

  useEffect(() => {
    if (!linkedCustomerId) return undefined;
    let active = true;

    api.get(`/admin/users/customers/${linkedCustomerId}`, {
      headers: getPermissionHeaders("view"),
    }).then(async (response) => {
      if (!active || !response.data?.data) return;
      const customer = response.data.data;
      setSelectedCustomer(customer);
      setCustomerOrders([]);
      setOrdersSidebarOpen(false);
      setViewSidebarOpen(true);
      await fetchCustomerOrders(customer.user_id);
    }).catch((error) => {
      if (active) showRequestError(error, "Failed to open the linked customer.");
    });

    return () => { active = false; };
  }, [linkedCustomerId]);

  const updateCustomerStatus = (customer, newStatus) => {
    confirmDialog({
      message: `Change ${customer.name}'s status to "${newStatus}"?`,
      header: "Update Customer Status",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await api.patch(`/admin/users/customers/${customer.user_id}/status`, {
            status: newStatus,
          }, {
            headers: getPermissionHeaders("edit"),
          });

          toast.success("Customer status updated.");
          await fetchCustomers(pagination.page);

          if (selectedCustomer?.user_id === customer.user_id) {
            setSelectedCustomer((prev) => ({
              ...prev,
              status: newStatus,
            }));
          }
        } catch (error) {
          showRequestError(error, "Status update failed.");
        }
      },
    });
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const getStatusClass = (value) => {
    return value || "pending";
  };

  const renderAddress = (address) => {
    return (
      <div className="customer-address-card" key={address.user_detail_id}>
        <div>
          <strong>{address.address_type}</strong>
          {address.is_default && <span className="status-badge active">Default</span>}
        </div>

        <p>{address.phone_no}</p>
        <p>
          {address.address1}
          {address.address2 ? `, ${address.address2}` : ""}
          <br />
          {address.city}, {address.state}
          <br />
          {address.country} - {address.zipcode}
        </p>
      </div>
    );
  };


  const normaliseSelectedOptions = (selectedOptions) => {
    if (!selectedOptions) return [];

    let options = selectedOptions;

    if (typeof options === "string") {
      try {
        options = JSON.parse(options);
      } catch {
        return [];
      }
    }

    if (Array.isArray(options)) {
      return [...options].sort(
        (a, b) =>
          Number(a?.sort_order || 0) -
          Number(b?.sort_order || 0)
      );
    }

    if (typeof options === "object") {
      return Object.entries(options).map(([key, value], index) => {
        if (value && typeof value === "object") {
          return {
            ...value,
            field_name:
              value.field_name ||
              value.template_name ||
              key,
            label:
              value.label ||
              value.value ||
              value.value_key ||
              "-",
            sort_order:
              value.sort_order ?? index,
          };
        }

        return {
          field_name: key,
          label: value,
          sort_order: index,
        };
      });
    }

    return [];
  };

  const formatOptionValue = (option) => {
    const value =
      option?.label ??
      option?.value ??
      option?.value_key ??
      "-";

    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "object"
            ? item.label ||
            item.value ||
            item.name ||
            JSON.stringify(item)
            : String(item)
        )
        .join(", ");
    }

    if (value && typeof value === "object") {
      return (
        value.label ||
        value.value ||
        value.name ||
        JSON.stringify(value)
      );
    }

    return String(value);
  };


  const renderOrderDetails = (order) => {
    return (
      <details className="customer-order-detail-card" key={order.order_id}>
        <summary className="customer-order-summary">
          <span className="customer-order-icon">
            <TbPackage />
          </span>

          <span className="customer-order-summary-main">
            <strong>{order.order_number}</strong>
            <small>
              {formatDate(order.createdAt)} · {(order.items || []).length} product(s)
            </small>
          </span>

          <span className="customer-order-summary-statuses">
            <span className={`status-badge ${getStatusClass(order.order_status)}`}>
              {order.order_status}
            </span>
            <span className={`status-badge ${getStatusClass(order.payment_status)}`}>
              {order.payment_status}
            </span>
          </span>

          <span className="customer-order-summary-total">
            <strong>{formatCurrency(order.total_amount)}</strong>
            <small>View details</small>
          </span>
        </summary>

        <div className="customer-order-expanded">
          <div className="customer-order-price-grid">
            <div>
              <span>Subtotal</span>
              <strong>{formatCurrency(order.subtotal)}</strong>
            </div>

            <div>
              <span>Shipping</span>
              <strong>{formatCurrency(order.shipping_fee)}</strong>
            </div>

            <div>
              <span>Platform Fee</span>
              <strong>{formatCurrency(order.platform_fee)}</strong>
            </div>

            <div>
              <span>Total</span>
              <strong>{formatCurrency(order.total_amount)}</strong>
            </div>
          </div>

          {(order.items || []).map((item) => {
            const snapshot = item.product_snapshot || {};

            return (
            <div className="customer-order-product" key={item.order_item_id}>
              <img src={snapshot.image || "/img/noimage.jpg"} alt={snapshot.name || "Product"} />

              <div>
                <strong>{snapshot.name || "Product"}</strong>

                {snapshot.sku && <p>SKU: {snapshot.sku}</p>}

                <p>Quantity: {item.quantity}</p>
                <p>Amount: {formatCurrency(item.total_price)}</p>

                {normaliseSelectedOptions(item.selected_options).length > 0 && (
                  <div className="customer-order-options">
                    <b>Selected Options</b>

                    {normaliseSelectedOptions(item.selected_options).map(
                      (option, index) => {
                        const optionName =
                          option.field_name ||
                          option.template_name ||
                          option.field_key ||
                          `Option ${index + 1}`;

                        const priceAdjustment = Number(
                          option.price_adjustment || 0
                        );

                        return (
                          <small
                            key={
                              option.value_id ||
                              option.field_id ||
                              `${optionName}-${index}`
                            }
                          >
                            <span>{optionName}:</span>{" "}
                            <strong>{formatOptionValue(option)}</strong>

                            {priceAdjustment > 0 && (
                              <em> (+{formatCurrency(priceAdjustment)})</em>
                            )}
                          </small>
                        );
                      }
                    )}
                  </div>
                )}

                {(item.form_values || []).length > 0 && (
                  <div className="customer-order-form-data">
                    <b>Submitted Details</b>

                    {(item.form_values || []).map((field) => (
                      <small key={field.order_item_form_value_id}>
                        {field.field_label}:{" "}
                        {field.input_type === "file" && field.file_url ? (
                          <a href={field.file_url} target="_blank" rel="noreferrer">
                            {field.file_name || "View file"}
                          </a>
                        ) : (
                          field.value || "-"
                        )}
                      </small>
                    ))}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </details>
    );
  };

  return (
    <section className="admin-page customers-page premium-users-page">
      <ConfirmDialog />

      <div className="page-header users-hero-header">
        <div>
          <p>Customer Management</p>
          <h1>Customers</h1>
        </div>
        <div className="page-total-summary">
          <span>Total:</span>
          <strong>{pagination.total || customers.length}</strong>
        </div>
      </div>

      {/* <div className="users-summary-strip">
        <div>
          <span>Total Customers</span>
          <strong>{pagination.total || customers.length}</strong>
        </div>

        <div>
          <span>Active This Page</span>
          <strong>{activeCustomers}</strong>
        </div>

        <div>
          <span>Blocked This Page</span>
          <strong>{blockedCustomers}</strong>
        </div>
      </div> */}

      <div className="page-card premium-user-card">
        <div className="premium-user-toolbar">
          <div>
            <h2>Customer Directory</h2>
            <p>
              {loading
                ? "Loading customers..."
                : `${pagination.total} customer(s) found.`}
            </p>
          </div>

          <div className="premium-user-filters">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search name or email..."
            />

            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>

        <div className={`directory-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="directory-data-table customer-directory-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Saved addresses</th>
                <th>Status</th>
                <th>Joined</th>
                <th>Last updated</th>
                <th aria-label="Actions">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!loading && customers.length === 0 && (
                <tr>
                  <td className="directory-table-empty" colSpan="6">
                    <TbPackage />
                    <strong>No customers found</strong>
                    <span>Try changing your filters or search term.</span>
                  </td>
                </tr>
              )}

              {customers.map((customer) => {
                const defaultAddress = (customer.details || []).find(
                  (address) => address.is_default
                );

                return (
                  <tr key={customer.user_id}>
                    <td className="directory-person-cell">
                      <div className="directory-person">
                        {customer.image_url ? (
                          <img src={customer.image_url} alt="" />
                        ) : (
                          <span className="directory-avatar">
                            {customer.name?.charAt(0)?.toUpperCase() || "C"}
                          </span>
                        )}

                        <div>
                          <strong>{customer.name}</strong>
                          <span>{customer.email}</span>
                          <small>Customer #{customer.user_id}</small>
                        </div>
                      </div>
                    </td>

                    <td className="directory-address-cell">
                      <strong>{(customer.details || []).length}</strong>
                      <span>
                        {defaultAddress
                          ? `${defaultAddress.city || ""}${
                              defaultAddress.state ? `, ${defaultAddress.state}` : ""
                            }`
                          : "No saved address"}
                      </span>
                    </td>

                    <td>
                      <select
                        className={`directory-status-select status-${customer.status}`}
                        value={customer.status}
                        aria-label={`Change ${customer.name}'s status`}
                        onChange={(event) =>
                          updateCustomerStatus(customer, event.target.value)
                        }
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                        <option value="blocked">Blocked</option>
                      </select>
                    </td>

                    <td className="directory-date-cell">
                      <strong>{formatDate(customer.createdAt)}</strong>
                      <span>Account created</span>
                    </td>

                    <td className="directory-date-cell">
                      <strong>{formatDate(customer.updatedAt)}</strong>
                      <span>Profile update</span>
                    </td>

                    <td className="directory-actions-cell">
                      <div className="directory-row-actions">
                        <button
                          type="button"
                          title="View customer"
                          aria-label={`View ${customer.name}`}
                          onClick={() => openViewCustomer(customer)}
                        >
                          <TbEyeSearch />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="directory-pagination">
          <button
            type="button"
            className="directory-pagination-direction"
            disabled={pagination.page <= 1 || loading}
            onClick={() => fetchCustomers(pagination.page - 1)}
          >
            <GrPrevious /> Previous
          </button>

          <div className="directory-pagination-pages">
            {getPaginationItems(pagination.page, pagination.totalPages).map(
              (item) =>
                typeof item === "string" ? (
                  <span key={item}>…</span>
                ) : (
                  <button
                    type="button"
                    className={item === pagination.page ? "active" : ""}
                    key={item}
                    disabled={loading}
                    aria-label={`Go to page ${item}`}
                    aria-current={item === pagination.page ? "page" : undefined}
                    onClick={() => fetchCustomers(item)}
                  >
                    {item}
                  </button>
                )
            )}
          </div>

          <button
            type="button"
            className="directory-pagination-direction"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => fetchCustomers(pagination.page + 1)}
          >
            Next <GrNext />
          </button>
        </div>
      </div>

      <Sidebar
        visible={viewSidebarOpen}
        position="right"
        onHide={() => setViewSidebarOpen(false)}
        className="premium-user-sidebar customer-detail-sidebar"
      >
        {selectedCustomer && (
          <div className="user-view-shell">
            <div className="user-view-hero">
              {selectedCustomer.image_url ? (
                <img src={selectedCustomer.image_url} alt={selectedCustomer.name} />
              ) : (
                <div>{selectedCustomer.name?.charAt(0)?.toUpperCase() || "C"}</div>
              )}
            </div>

            <div className="user-view-title">
              <span>Customer Account</span>
              <h2>{selectedCustomer.name}</h2>
              <p>{selectedCustomer.email}</p>
            </div>

            <div className="user-view-meta">
              <div>
                <strong>User ID</strong>
                <span>{selectedCustomer.user_id}</span>
              </div>

              <div>
                <strong>Status</strong>
                <span className={`status-badge ${selectedCustomer.status}`}>
                  {selectedCustomer.status}
                </span>
              </div>

              <div>
                <strong>Joined</strong>
                <span>{formatDate(selectedCustomer.createdAt)}</span>
              </div>

              <div>
                <strong>Updated</strong>
                <span>{formatDate(selectedCustomer.updatedAt)}</span>
              </div>
            </div>

            <div className="customer-restrict-box">
              <h3>Saved Addresses</h3>

              {selectedCustomer.details?.length > 0 ? (
                selectedCustomer.details.map((address) => renderAddress(address))
              ) : (
                <p>No saved addresses available.</p>
              )}
            </div>

            <div className="customer-order-preview-card">
              <span className="customer-order-preview-icon">
                <TbPackage />
              </span>

              <div>
                <span>Purchase history</span>
                <h3>
                  {ordersLoading
                    ? "Loading orders..."
                    : `${customerOrders.length} order${
                        customerOrders.length === 1 ? "" : "s"
                      } placed`}
                </h3>
                <p>
                  Open order history in a separate panel to keep this profile concise.
                </p>
              </div>

              <button
                type="button"
                className="primary-action"
                disabled={ordersLoading}
                onClick={() => {
                  setViewSidebarOpen(false);
                  setOrdersSidebarOpen(true);
                }}
              >
                View orders
              </button>
            </div>

            <div className="customer-restrict-box">
              <h3>Access Control</h3>
              <p>
                Blocking a customer will prevent them from accessing protected
                customer services.
              </p>

              <select
                value={selectedCustomer.status}
                onChange={(event) =>
                  updateCustomerStatus(selectedCustomer, event.target.value)
                }
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>

            <div className="user-view-actions">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() => setViewSidebarOpen(false)}
              >
                Close
              </button>

              <button
                type="button"
                className="danger-action bottonborder"
                onClick={() => updateCustomerStatus(selectedCustomer, "blocked")}
                disabled={selectedCustomer.status === "blocked"}
              >
                Restrict Customer
              </button>
            </div>
          </div>
        )}
      </Sidebar>

      <Sidebar
        visible={ordersSidebarOpen}
        position="right"
        onHide={() => setOrdersSidebarOpen(false)}
        className="customer-orders-sidebar"
      >
        {selectedCustomer && (
          <div className="customer-orders-shell">
            <div className="customer-orders-header">
              <button
                type="button"
                className="customer-orders-back"
                onClick={() => {
                  setOrdersSidebarOpen(false);
                  setViewSidebarOpen(true);
                }}
              >
                <GrPrevious /> Customer profile
              </button>

              <span>Order history</span>
              <h2>{selectedCustomer.name}</h2>
              <p>{selectedCustomer.email}</p>
            </div>

            <div className="customer-orders-stats">
              <div>
                <span>Total orders</span>
                <strong>{customerOrders.length}</strong>
              </div>
              <div>
                <span>Products ordered</span>
                <strong>{orderSummary.items}</strong>
              </div>
              <div>
                <span>Total order value</span>
                <strong>{formatCurrency(orderSummary.total)}</strong>
              </div>
            </div>

            <div className="customer-orders-list-heading">
              <div>
                <h3>Orders</h3>
                <p>Select an order to see its products and submitted details.</p>
              </div>
              <span>{customerOrders.length} records</span>
            </div>

            <div className="customer-orders-list">
              {ordersLoading ? (
                <div className="customer-orders-empty">Loading order history...</div>
              ) : customerOrders.length === 0 ? (
                <div className="customer-orders-empty">
                  <TbPackage />
                  <strong>No orders placed yet</strong>
                  <span>This customer's orders will appear here.</span>
                </div>
              ) : (
                customerOrders.map((order) => renderOrderDetails(order))
              )}
            </div>
          </div>
        )}
      </Sidebar>
    </section>
  );
};

export default Customers;
